import dayjs from 'dayjs'
import { UserData } from '../types/user/types'
import { dateCorrectFallback, wordsCorrectFallback } from './cognitionCriteria'

export type Cell = string | number | null
type Column = { header: string; value: (record: any) => Cell }
type Domain = { sheet: string; key: keyof UserData; columns: Column[] }

const yesNo = (value: unknown): Cell => (value === undefined || value === null ? null : value ? 'Yes' : 'No')
const correctWrong = (value: boolean | null): Cell => (value === null ? null : value ? 'Correct' : 'Wrong')
const recordTime = (record: any): string | undefined => record?.creationTimestamp || record?.testTimestamptz
const status = (record: any): Cell => record?.otherInformation?.statusLabel ?? null
const recalledWords = (record: any): string =>
  record.recalledThreeWordsInOneTextbox ? String(record.recalledThreeWordsInOneTextbox) : (record.recalledThreeWords ?? []).join(', ')

const AD8_QUESTIONS: [string, string][] = [
  ['problemsWithJudgment', 'Problems With Judgment'],
  ['lessInterestInHobbies', 'Less Interest In Hobbies'],
  ['repeatTheSameThings', 'Repeats The Same Things'],
  ['troubleLearningTools', 'Trouble Learning Tools'],
  ['forgetCorrectDate', 'Forgets Correct Date'],
  ['troubleHandlingFinancials', 'Trouble Handling Financials'],
  ['troubleRememberingAppointments', 'Trouble Remembering Appointments'],
  ['dailyProblemsWithThinking', 'Daily Problems With Thinking'],
]

const toNumber = (value: unknown): Cell => {
  if (value === null || value === undefined || value === '') return null
  const number = Number(value)
  return Number.isNaN(number) ? null : number
}

const CIRCLE_DURATIONS: [string, string][] = [
  ['firstCircleTimeDuration', 'Rise 1 (s)'],
  ['secondCircleTimeDuration', 'Rise 2 (s)'],
  ['thirdCircleTimeDuration', 'Rise 3 (s)'],
  ['fourthCircleTimeDuration', 'Rise 4 (s)'],
  ['fifthCircleTimeDuration', 'Rise 5 (s)'],
]

const HEALTH_FLAGS: [string, string][] = [
  ['havingIllnesses', 'Illnesses'],
  ['havingGeneralFrailty', 'General Frailty'],
  ['havingWeakLegMuscles', 'Weak Leg Muscles'],
  ['havingBalanceIssues', 'Balance Issues'],
  ['havingDizzinessIssues', 'Dizziness'],
  ['havingBreathingProblems', 'Breathing Problems'],
  ['havingHeartProblems', 'Heart Problems'],
  ['havingPastStroke', 'Past Stroke'],
  ['havingParkinsons', "Parkinson's"],
  ['havingKneeProblem', 'Knee Problem'],
  ['HavingHipProblem', 'Hip Problem'],
  ['HavingLowBackProblem', 'Low Back Problem'],
  ['havingRecentLegBackInjury', 'Recent Leg/Back Injury'],
  ['surgeryWith6Months', 'Surgery Within 6 Months'],
  ['pregnant', 'Pregnant'],
]

const hearingTrialsPassed = (record: any): number =>
  (record?.digits ?? []).filter((trial: string, x: number) =>
    Array.from(trial).every((digit, y) => digit === record.userResponse?.[x]?.[y])
  ).length

const DOMAINS: Domain[] = [
  {
    sheet: 'Cognition',
    key: 'cognition',
    columns: [
      { header: 'Status', value: status },
      { header: 'Reported Memory Issue', value: (r) => yesNo(r.havingIssueWithMemory) },
      { header: 'Words Recalled', value: (r) => recalledWords(r) || 'No answer' },
      {
        header: 'Word Recall',
        value: (r) =>
          correctWrong(
            !recalledWords(r)
              ? false
              : typeof r.otherInformation?.threeWordsCorrect === 'boolean'
                ? r.otherInformation.threeWordsCorrect
                : wordsCorrectFallback(r)
          ),
      },
      {
        header: 'Stated Date',
        value: (r) =>
          correctWrong(
            typeof r.otherInformation?.dateCorrect === 'boolean'
              ? r.otherInformation.dateCorrect
              : dateCorrectFallback(r.selectedDate, r.creationTimestamp)
          ),
      },
    ],
  },
  {
    sheet: 'Mobility',
    key: 'sitToStand',
    columns: [
      { header: 'Status', value: (r) => status(r) ?? 'Unable to Predict' },
      { header: 'Completed 5 Rises', value: (r) => yesNo(r.otherInformation?.finished5ChairRises) },
      {
        header: 'Time Taken (s)',
        value: (r) => {
          if (r.otherInformation?.finished5ChairRises === false) return null
          const time = r.otherInformation?.activeDuration ?? r.otherInformation?.totalTimeTaken
          return typeof time === 'number' ? Number(time.toFixed(2)) : null
        },
      },
      ...CIRCLE_DURATIONS.map(([field, header]) => ({ header, value: (r: any) => toNumber(r.otherInformation?.[field]) })),
      {
        header: 'Finished At',
        value: (r) => {
          const t = r.otherInformation?.finishedTimestamp
          if (typeof t !== 'number') return null
          return dayjs(t < 1e12 ? t * 1000 : t).format('YYYY-MM-DD HH:mm:ss')
        },
      },
      { header: 'Project', value: (r) => r.otherInformation?.project ?? null },
      { header: 'Collected From Real User', value: (r) => yesNo(r.otherInformation?.collectedFromRealUser) },
      { header: 'Age', value: (r) => r.otherInformation?.age ?? null },
      { header: 'Gender', value: (r) => r.otherInformation?.gender ?? null },
      { header: 'Height', value: (r) => r.otherInformation?.height ?? null },
      { header: 'Weight', value: (r) => r.otherInformation?.weight ?? null },
      { header: 'Cognitive Decline', value: (r) => r.otherInformation?.cognitiveDecline ?? null },
      ...HEALTH_FLAGS.map(([field, header]) => ({ header, value: (r: any) => yesNo(r.otherInformation?.[field]) })),
    ],
  },
  {
    sheet: 'Nutrition',
    key: 'nutrition',
    columns: [
      { header: 'Status', value: status },
      { header: 'Loss of Weight', value: (r) => yesNo(r.havingLossOfWeight) },
      { header: 'Loss of Appetite', value: (r) => yesNo(r.havingLossOfAppetite) },
    ],
  },
  {
    sheet: 'Vision',
    key: 'vision',
    columns: [
      { header: 'Status', value: status },
      { header: 'Eye Diseases', value: (r) => yesNo(r.havingEyeDiseases) },
      { header: 'Difficulty Seeing Far', value: (r) => yesNo(r.havingDifficultySeeingFar) },
      { header: 'On Treatment', value: (r) => yesNo(r.onTreatment) },
      { header: 'Vision Problem', value: (r) => yesNo(r.havingEyeProblems) },
    ],
  },
  {
    sheet: 'Hearing',
    key: 'hearing',
    columns: [
      { header: 'Status', value: status },
      { header: 'Hearing Aid Reported', value: (r) => yesNo(!!r.otherInformation?.wearingHearingAid) },
      {
        header: 'Trials Passed',
        value: (r) => (r.otherInformation?.wearingHearingAid || !r.digits?.length ? null : hearingTrialsPassed(r)),
      },
      { header: 'Total Trials', value: (r) => (r.otherInformation?.wearingHearingAid ? null : r.digits?.length ?? null) },
    ],
  },
  {
    sheet: 'Mood',
    key: 'mood',
    columns: [
      { header: 'Status', value: status },
      { header: 'Feeling Down', value: (r) => yesNo(r.feelingDown) },
      { header: 'Little Interest', value: (r) => yesNo(r.experiencingLittleInterest) },
    ],
  },
  {
    sheet: 'AD8',
    key: 'ad8',
    columns: [
      { header: 'Status', value: status },
      {
        header: 'Score (Yes count / 8)',
        value: (r) => AD8_QUESTIONS.filter(([field]) => String(r[field]).toLowerCase() === 'yes').length,
      },
      ...AD8_QUESTIONS.map(([field, header]) => ({ header, value: (r: any) => (r[field] ?? null) as Cell })),
    ],
  },
  {
    sheet: 'Handgrip',
    key: 'handgrip',
    columns: [
      { header: 'Status', value: status },
      { header: 'Gender', value: (r) => r.otherInformation?.gender ?? null },
      { header: 'Left Hand (kg)', value: (r) => r.leftHandReading ?? null },
      { header: 'Right Hand (kg)', value: (r) => r.rightHandReading ?? null },
    ],
  },
  {
    sheet: 'Exhaustion',
    key: 'exhaustion',
    columns: [
      { header: 'Status', value: status },
      { header: 'Everything Requires Effort', value: (r) => r.everythingRequireEffort ?? null },
      { header: 'Cannot Get Going', value: (r) => r.cannotGetGoing ?? null },
    ],
  },
  {
    sheet: 'Gait Speed',
    key: 'gaitSpeed',
    columns: [
      { header: 'Status', value: status },
      { header: 'Reading (m/s)', value: (r) => r.reading ?? null },
    ],
  },
  {
    sheet: 'Physical Activity',
    key: 'physicalActivity',
    columns: [
      { header: 'Status', value: status },
      { header: 'Time Spent Walking', value: (r) => r.timeSpentInWalking ?? null },
    ],
  },
  {
    sheet: 'Blood Pressure',
    key: 'bloodPressure',
    columns: [
      { header: 'Systolic', value: (r) => r.systolic ?? null },
      { header: 'Diastolic', value: (r) => r.diastolic ?? null },
      { header: 'Heart Rate', value: (r) => r.heartRate ?? null },
    ],
  },
]

export type ExportPatient = { name: string; userId: string; account: string; data: UserData }
export type ExportSheet = { name: string; headers: string[]; widths: number[]; rows: Cell[][] }
export type ExportBundle = { fileName: string; sheets: ExportSheet[] }

const safeFileName = (name: string) => name.trim().replace(/[\\/:*?"<>|]/g, '_')

const buildSheets = (patients: ExportPatient[], includePatientColumns: boolean): ExportSheet[] =>
  DOMAINS.map(({ sheet, key, columns }) => {
    const headers = [
      ...(includePatientColumns ? ['Patient', 'Patient UID', 'Account'] : []),
      'Date',
      ...columns.map((column) => column.header),
    ]
    const widths = [
      ...(includePatientColumns ? [22, 28, 26] : []),
      18,
      ...columns.map((column) => Math.max(14, column.header.length + 4)),
    ]
    const rows: Cell[][] = []
    patients.forEach((patient) => {
      const records = [...(((patient.data as any)[key] as any[]) ?? [])].sort(
        (a, b) => dayjs(recordTime(a)).valueOf() - dayjs(recordTime(b)).valueOf()
      )
      records.forEach((record) => {
        const time = recordTime(record)
        rows.push([
          ...(includePatientColumns ? [patient.name, patient.userId, patient.account] : []),
          time ? dayjs(time).format('YYYY-MM-DD HH:mm') : null,
          ...columns.map((column) => column.value(record)),
        ])
      })
    })
    return { name: sheet, headers, widths, rows }
  })

export const buildPatientExport = (userData: UserData, patientName: string): ExportBundle => ({
  fileName: `${safeFileName(patientName) || 'patient'}_history_${dayjs().format('YYYY-MM-DD')}.xlsx`,
  sheets: buildSheets([{ name: patientName, userId: '', account: '', data: userData }], false),
})

export const buildAllPatientsExport = (patients: ExportPatient[]): ExportBundle => ({
  fileName: `all_patients_history_${dayjs().format('YYYY-MM-DD')}.xlsx`,
  sheets: buildSheets([...patients].sort((a, b) => a.name.localeCompare(b.name)), true),
})

export const toXlsxBuffer = async ({ sheets }: ExportBundle) => {
  const { Workbook } = await import('exceljs')
  const workbook = new Workbook()
  sheets.forEach(({ name, headers, widths, rows }) => {
    const worksheet = workbook.addWorksheet(name)
    worksheet.columns = headers.map((header, i) => ({ header, width: widths[i] }))
    worksheet.getRow(1).font = { bold: true }
    worksheet.views = [{ state: 'frozen', ySplit: 1 }]
    rows.forEach((row) => worksheet.addRow(row))
  })
  return workbook.xlsx.writeBuffer()
}

export const downloadExport = async (bundle: ExportBundle) => {
  const buffer = await toXlsxBuffer(bundle)
  const url = URL.createObjectURL(new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }))
  const link = document.createElement('a')
  link.href = url
  link.download = bundle.fileName
  link.click()
  URL.revokeObjectURL(url)
}
